/**
 * Command sequence runs, built from the progress reports VTS emits while a
 * schema v8 channel-modifier program runs (`sequenceProgressBus`).
 *
 * Every report carries the run's whole state — its status and every step's — so
 * a run is simply its latest report, keyed by run id. A run still `Running` is
 * active; any other status ends it, and it moves to the bounded list of recent
 * runs. A finished run is never reopened by a late `Running` report.
 */
import formatUuidT from "../../../flatbuffers/display/uuid";
import type { FrontEndMessageT } from "../../../flatbuffers/generated/front-end/front-end-message";
import type { SequenceProgressT } from "../../../flatbuffers/generated/front-end/sequence-progress";
import { SequenceRunStatus } from "../../../flatbuffers/generated/front-end/sequence-run-status";
import type { SequenceProgressEvent } from "../socketMessageState/sequenceProgressBus";

/** How many finished runs are kept, newest first. */
export const RECENT_RUNS = 50;

/** One run, as its latest progress report described it. */
export interface SequenceRunView {
  runId: string;
  modifierId: string;
  modifierName: string;
  bankId: string;
  liftId: string;
  status: SequenceRunStatus;
  /** The latest report's `sequence` table: steps, elapsed time, detail. */
  progress: SequenceProgressT;
  /** The message the run was started for, as the report carried it. */
  commanded: FrontEndMessageT;
  /** When this browser first heard of the run. */
  firstSeenAtMs: number;
  /** When the latest report arrived; the active step's time counts on from here. */
  receivedAtMs: number;
}

export interface SequenceRunsState {
  /** Runs still `Running`, by run id. */
  active: ReadonlyMap<string, SequenceRunView>;
  /** Finished runs, newest first, at most {@link RECENT_RUNS}. */
  recent: readonly SequenceRunView[];
}

export const EMPTY_SEQUENCE_RUNS: SequenceRunsState = { active: new Map(), recent: [] };

function text(value: string | Uint8Array | null): string {
  return typeof value === "string" ? value : "";
}

/** Folds one progress report into the runs. */
export function reduceSequenceRuns(
  state: SequenceRunsState,
  event: SequenceProgressEvent,
): SequenceRunsState {
  const { fem, receivedAtMs } = event;
  const progress = fem.sequence;
  if (!progress?.runId) return state;
  const runId = formatUuidT(progress.runId);

  const previous = state.active.get(runId) ?? state.recent.find((run) => run.runId === runId);
  const reopens =
    previous !== undefined &&
    previous.status !== SequenceRunStatus.Running &&
    progress.status === SequenceRunStatus.Running;
  if (reopens) return state;

  const view: SequenceRunView = {
    runId,
    modifierId: formatUuidT(progress.modifierId),
    modifierName: text(progress.modifierName),
    bankId: formatUuidT(fem.bankId),
    liftId: formatUuidT(fem.liftId),
    status: progress.status,
    progress,
    commanded: fem,
    firstSeenAtMs: previous?.firstSeenAtMs ?? receivedAtMs,
    receivedAtMs,
  };

  const active = new Map(state.active);
  if (progress.status === SequenceRunStatus.Running) {
    active.set(runId, view);
    return { active, recent: state.recent };
  }
  active.delete(runId);
  const recent = [view, ...state.recent.filter((run) => run.runId !== runId)].slice(0, RECENT_RUNS);
  return { active, recent };
}
